/**
 * Every word the extraction card says — Stories 20.2 and 20.10.
 *
 * Kept apart by WHERE EACH WORD COMES FROM, because each kind is held to a
 * different source by `tests/extraction-copy.test.ts`:
 *
 *   DRAWN     `design/screens/editor-qualify.html` draws it. Held to the mockup,
 *             verbatim.
 *   PROMPTED  the M2 prompt describes it and no capture draws it. Held to
 *             `design/claude-design-prompts.md`, verbatim.
 *   COMPOSED  neither draws nor describes it: the messages that say how to fix a
 *             field before it is added, and the states no capture reaches. They
 *             are built from the tab's existing controls on DECISION-054 and
 *             -077's precedent, and listed here so a round-trip replaces them in
 *             one place.
 *
 * THE EDITOR `Add a field` OPENS WAS COMPOSED UNTIL 2026-09-17. Story 20.2 built
 * it from the prompt and the contract before any capture drew it. The second M2
 * round-trip drew it (`field-editor`) with every label 20.2 had composed,
 * verbatim, plus a line 20.2 never had, under What to pull out. Story 20.10 moved
 * those labels into DRAWN, so the test holds them to the drawing and not only to
 * RULE-021.
 *
 * All three are held to RULE-021 by the same test: "The Qualify UI asks what to
 * pull out of an answer, never whether an answer is good."
 *
 * NO RUNTIME IMPORTS. The type import below is erased, so a node-side test can
 * load this file without resolving the workspace.
 */
import type { ExtractionField } from '@leadgible/schema'

export type ExtractionType = ExtractionField['type']

export const DRAWN = {
  title: 'What to pull out of long answers',
  lead: 'Name the facts you want read out of what people write. Each one is a typed field.',
  from: 'From',
  scoredBy: 'Scored by',
  notScored: 'Read, not scored',
  remove: 'Remove',
  add: 'Add a field',
  emptyTitle: 'Nothing is being pulled out of long answers yet.',
  emptyBody:
    'Name a fact you want read out of what people write — a budget, a timeline, a number of staff.',
  /** The rules-only capture's line under the heading (Story 18.8's state). */
  notRead: 'Not being read right now.',

  // The editor `Add a field` opens — the `field-editor` capture (Story 20.10).
  /** The editor's own heading, above Name. */
  newField: 'New field',
  name: 'Name',
  description: 'What to pull out',
  /** The supporting line under What to pull out: what the model is told to read for. */
  descriptionHelper: 'In your own words. This is what the answer is read for.',
  type: 'Type',
  lowest: 'Lowest',
  highest: 'Highest',
  values: 'The values it can take',
  /** The Qualify tab's own words for a comma-separated list, one card down. */
  valuesHelper: 'Separate entries with a comma.',
  source: 'Read from',
  chooseSource: 'Choose a question',
  /**
   * The source select's helper, both of the drawing's sentences.
   *
   * The second names the Build tab's "Pull facts out of it", and it was withheld
   * until Story 20.7 built that control: a sentence pointing at a control the
   * dashboard did not have would have sent an operator looking for it (Q-101).
   * Straight quotes where the drawing has curly ones — DECISION-118.
   */
  sourceHelper:
    'Only long-text questions set to extraction can be read. To add one here, choose "Pull facts out of it" for a long-text question on the Build tab.',
  submit: 'Add this field',
  cancel: 'Cancel',

  /** "Add a criterion"'s extraction group — the `points` capture draws it open (Story 20.3). */
  fromField: 'From an extracted field',
} as const

/** The drawn aside, "3 of 50 fields". The limit is an argument so this file needs no schema at runtime. */
export const fieldsCount = (count: number, limit: number): string => `${count} of ${limit} fields`

/**
 * The drawn order — "Text · Number · One of · Yes/no · Date" — which is the
 * order the type select offers them in. The rows draw the first three, and the
 * `field-editor` capture's Type select draws all five.
 */
export const TYPE_ORDER: readonly ExtractionType[] = ['string', 'number', 'enum', 'boolean', 'date']

export const TYPE_LABEL: Record<ExtractionType, string> = {
  string: 'Text',
  number: 'Number',
  enum: 'One of',
  boolean: 'Yes/no',
  date: 'Date',
}

export const PROMPTED = {
  /** A removal refused because one criterion scores the field. */
  refusedByCriterion: (criterion: string): string =>
    `${criterion} scores this field. Remove that criterion first.`,
  /**
   * "Add a criterion"'s question group — "visibly labelled so the source is
   * never ambiguous" (Story 20.3). The `points` capture leaves it out, because
   * every question there already has a criterion and a group with nothing to
   * offer is not drawn.
   */
  fromQuestion: 'From a question',
} as const

export const COMPOSED = {
  /** "Add a criterion" with nothing left to offer, once any extraction field exists (Story 20.3). */
  everythingScored: 'Every question and extracted field already feeds the score.',
  atLimit: (limit: number): string => `All ${limit} fields are in use. Remove one to add another.`,
  orMore: (min: number): string => `${min} or more`,
  upTo: (max: number): string => `up to ${max}`,
  refusedByCriteria: (criteria: string): string =>
    `${criteria} score this field. Remove those criteria first.`,
  refusedByRule: 'A rule reads this field. Remove that rule first.',
  needsName: 'Give this field a name.',
  nameTooLong: (max: number): string => `Keep the name to ${thousands(max)} characters.`,
  needsDescription: 'Say what to pull out, in your own words.',
  descriptionTooLong: (max: number): string => `Keep this to ${thousands(max)} characters.`,
  needsValues: 'List at least two values, separated by a comma.',
  needsNumbers: 'Use numbers for the lowest and highest values.',
  rangeBackwards: 'The lowest value is above the highest. Swap them, or clear one.',
  needsSource: 'Choose the question to read from.',
} as const

/** 2000 → "2,000", without leaning on the runtime's locale data. */
function thousands(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}
